import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Participant, SourceDataEventStatus, SourceDataEventType, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { DateTime } from "luxon";
import { ComponentType, FC, memo, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { INoteLinkComponentProps, NoteLink, StatusListItemButtonTooltip, StatusListItemIconBox, StatusListItemTooltip, StatusListItemTooltipStats, StatusListStatsBox, getNoteCount } from './StatusListStats';
import useEvent from "@/hooks/form/source-data/useEvent";
import sourceDataEventStatusToText from "@/utilities/configuration/sourceDataEventStatusToText";
import sourceDataEventStatusToColor from "@/utilities/configuration/sourceDataEventStatusToColor";
import sourceDataEventStatusToIcon from "@/utilities/configuration/sourceDataEventStatusToIcon";
import { Link as RouteLink } from "react-router-dom";
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";

export type ParticipantSourceDataEventStatusListItemType = NonNullable<ReturnType<typeof useEvent>>

export interface IParticipantSourceDataEventStatusListAdditional {

    NoteLinkComponent?: ComponentType<INoteLinkComponentProps<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional>>
}

const ParticipantSourceDataNoteLinkComponent = memo(({
    itemData,
    status,
    type
}: INoteLinkComponentProps<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional>) => {
    const value = getNoteCount(itemData, type, status);

    if (value === undefined) {
        return (
            <>
                &mdash;
            </>
        )
    }
    
    let baseLink = `/${itemData?.data?.trial?.oid}${itemData?.data?.institution ? `/institutions/${itemData.data.institution.code}` : ''}/participants/${itemData?.data?.participant?.studyNumber}/events/${itemData?.data?.studyEventOid}/${itemData?.data?.studyEventRepeat}/notes-and-queries`;

    if (type && status) {
        baseLink += `?type=${type}&status=${status}`;
    }
    else if (type) {
        baseLink += `?type=${type}`;
    }
    else if (status) {
        baseLink += `?status=${status}`;
    }

    return (
        <NoteLink 
            //@ts-ignore
            component={RouteLink} 
            to={baseLink}
        >
            {value}
        </NoteLink>
    )
})

export interface IParticipantSourceDataEventStatusListProps extends StatusListProps<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> {
    trial?: Trial;
    institution?: Institution;
    participant?: Participant;
}


const EnrolmentBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5) 
}));

const InstBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.secondary.main,0.5) : lighten(theme.palette.secondary.main, 0.5)
}));

const createParticipantSourceDataEventStatusLabel: IStatusListCreateLabel<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={itemData.data.icon} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={
                    <>
                        {itemData.data.displayName}
                        {
                            itemData.data.eventMetadata.type === SourceDataEventType.Repeating && (
                                <>
                                    {' '}&mdash; {itemData.data.studyEventRepeat}
                                </>
                            )
                        }
                    </>
                } 
            />
        </>
    )
};

const createParticipantSourceDataEventLink: IStatusListCreateLink<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> = (itemData) => {
    return itemData.data.preview ? itemData.data.previewLink : itemData.data.dataLink;
};

const createParticipantSourceDataEventConfigurationLink: IStatusListCreateConfigureLink<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> = (itemData) => {
    return itemData.data.configurationLink;
};

const isParticipantSourceDataEventDisabled: IStatusListIsDisabled<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const ParticipantSourceDataEventStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationPriorityIndicatorStats itemData={itemData} theme={theme} />
            <StatusListItemButtonTooltip
                placement="left"
                title={
                    <>
                        <strong>Status</strong>
                        <div>
                            {sourceDataEventStatusToText(itemData.data.sourceDataEvent?.status)} 
                        </div>
                    </>
                }
            >
                <StatusListItemIconBox>
                    <Box 
                        sx={{ 
                            color: sourceDataEventStatusToColor(itemData.data.sourceDataEvent?.status)
                        }}
                    >
                        <FontAwesomeIcon 
                            icon={sourceDataEventStatusToIcon(itemData.data.sourceDataEvent?.status)} 
                        />
                    </Box>
                </StatusListItemIconBox>
            </StatusListItemButtonTooltip>
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Start Date</strong>
                        <Box>
                            {
                                itemData.data.sourceDataEvent?.startDate && (
                                    DateTime.fromISO(itemData.data.sourceDataEvent.startDate).toFormat('dd/MM/yyyy')
                                )
                            }
                            {
                                !itemData.data.sourceDataEvent?.startDate && (
                                    'Not Started'
                                )
                            }
                        </Box>
                    </>
                }
            >
                <EnrolmentBox>
                    {
                        itemData.data.sourceDataEvent?.startDate && (
                            DateTime.fromISO(itemData.data.sourceDataEvent.startDate).toFormat('dd/MM/yyyy')
                        )
                    }
                    {
                        !itemData.data.sourceDataEvent?.startDate && (
                            'Not Started'
                        )
                    }
                </EnrolmentBox>
            </StatusListItemTooltip>
            <DefaultStatComponents.FormStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.NoteStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReviewStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationStats itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional>[] = [
    {
        name: 'Event Order',
        sort: (a, b) => {
            if (a.data.eventMetadata.order === b.data.eventMetadata.order) {
                return a.data.studyEventRepeat! > b.data.studyEventRepeat! ? 1 : -1
            }
            else {
                return a.data.eventMetadata.order! > b.data.eventMetadata.order! ? 1 : -1
            }
        }
    },
    {
        name: 'Event Name',
        sort: (a, b) => {
            if (a.data.displayName === b.data.displayName) {
                return a.data.studyEventRepeat! > b.data.studyEventRepeat! ? 1 : -1
            }
            else {
                return a.data.displayName! > b.data.displayName! ? 1 : -1
            }
        }
    },
    DefaultSortableFields.priority,
    DefaultSortableFields.crfCompletion,
    DefaultSortableFields.openQueries,
    DefaultSortableFields.reviewCompletion,
    DefaultSortableFields.verificationCompletion,
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const createParticipantSourceDataEventAdditional: IStatusListCreateAdditional<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional> = (itemData) => {
    return {
        NoteLinkComponent: ParticipantSourceDataNoteLinkComponent
    }
};

const ParticipantSourceDataEventStatusList: FC<IParticipantSourceDataEventStatusListProps> = ({
    trial, 
    institution,
    participant,
    ...statusListProps
}) => {    

    const searchableFields: IStatusListSearch<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional, any, any>[] = [
            {
                key: 'studyNumber',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Study Number',
                searchFn: (item, value) => value ? item.data.participant?.studyNumber?.includes(value) ?? false : false
            } as IStatusListSearch<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional, string>
        ]

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The participant is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The participant is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The participant is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<ParticipantSourceDataEventStatusListItemType, IParticipantSourceDataEventStatusListAdditional, ConfigurationStatus[]>)

        return output;
    }, []);

    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])
    
    return (
        <StatusList
            {...statusListProps}
            createLabel={createParticipantSourceDataEventStatusLabel}
            createLink={createParticipantSourceDataEventLink}
            createConfigureLink={createParticipantSourceDataEventConfigurationLink}
            isDisabled={isParticipantSourceDataEventDisabled}
            createAdditional={createParticipantSourceDataEventAdditional}
            StatsComponent={ParticipantSourceDataEventStatsComponent}
            sortKey="participant-source-data-events-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Events</AlertTitle>
                    No events were found for the participant {participant?.studyNumber} on the {trial?.name}{institution ? ` at ${institution.name}` : ''}.
                </Alert> 
            }
            searchKey={`participant-source-data-events-search-${trial?.oid}-${institution?.code ?? 'All'}`}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default ParticipantSourceDataEventStatusList;